import { motion } from "framer-motion";
import { Lightbulb, ShieldCheck, Globe2 } from "lucide-react";
import { WORK_PILLARS } from "../../../data/careers";

const ICONS = [Lightbulb, ShieldCheck, Globe2];
// Amber for Innovation, forest for Quality, steel for global Reach — matches the
// three brand accents already in use across the site.
const ACCENTS = ["text-amber-accent", "text-forest-accent", "text-steel-accent"];
const DOTS = ["bg-amber-accent", "bg-forest-accent", "bg-steel-accent"];

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const WhyTheWork = () => {
  return (
    <section
      data-testid="careers-why-work"
      className="scroll-mt-nav py-20 md:py-28 border-t border-ink/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-6">
            <div className="overline text-forest-accent mb-4">/ 01 — The Work</div>
            <h2
              className="font-display font-bold leading-[1.02] tracking-tight text-ink text-balance"
              style={{ fontSize: "clamp(2rem, 4.8vw, 3.75rem)" }}
            >
              Chemistry that leaves
              <br />
              the lab and{" "}
              <span className="italic font-light text-amber-accent">finds the world.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 md:pt-3">
            <p className="text-ink-soft text-base md:text-lg leading-relaxed max-w-xl">
              We're a small enough team that your work is visible, and a large
              enough operation that it ends up in cargo containers on their way
              to Rotterdam, São Paulo or Ho Chi Minh City. If you want to
              design, analyse, audit or scale specialty chemistry — and see it
              travel — Milestone is a good place to spend the next chapter of
              your career.
            </p>
          </div>
        </div>

        {/* Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {WORK_PILLARS.map((p, i) => {
            const Icon = ICONS[i];
            return (
              <motion.article
                key={p.k}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i}
                data-testid={`work-pillar-${p.k.toLowerCase()}`}
                className="bg-bone p-8 md:p-10 group hover:bg-white transition-colors min-h-[280px] flex flex-col"
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon className={`w-6 h-6 ${ACCENTS[i]}`} strokeWidth={1.5} />
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${DOTS[i]}`} />
                    <span className={`overline ${ACCENTS[i]}`}>{p.k}</span>
                  </div>
                </div>
                <div className="mt-auto">
                  <h3 className="font-display font-medium text-ink text-xl md:text-2xl leading-tight mb-3 text-balance">
                    {p.title}
                  </h3>
                  <p className="text-ink-soft text-[15px] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
